import React, { useState, useRef } from 'react';
import { Switch, Drawer } from '../../../../components';
import style from './add-markdown.module.scss';
import { useMutate, useAppAndBranchId } from '../../../../hooks';
import { api } from '../../api';
import { useToast } from '../../../../../../../../helpers/commonFunction';
import MarkdownIcon from '../../../../../../../../assets/svg/solo-chat/markdown.svg';
import Editor from '@monaco-editor/react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { ContentMode } from '../../../../types';
import { validateList } from '../../../../utils/validation';
import { errorMessage } from '../../../../utils/error';

type Markdown = {
	title: string;
	markdownText: string;
};

type Props = {
	isOpen: boolean;
	close: () => void;
	mode?: ContentMode;
	initialValue?: Markdown & { id: string };
	refetchContent: () => void;
	onCreate?: (contentId: string) => void;
	isPreview?: boolean
};

export function AddMarkdown(props: Props) {
	const editorRef = useRef<any>(null);
	const { ErrorToast, WarningToast } = useToast();
	const [appId, branchId] = useAppAndBranchId();
	const { isOpen, close, initialValue, refetchContent, onCreate, isPreview = false } = props;
	const [state, setState] = useState<Markdown>(
		initialValue ?? {
			title: '',
			markdownText: '',
		},
	);
	const [mode, setMode] = useState(props.mode ?? 'create');
	const [markdownPreview, setIsMarkdownPreview] = useState(false);

	const { mutate: createMutate, loading: createLoading } = useMutate(
		api.createContent,
	);
	const { mutate: updateMutate, loading: updateLoading } = useMutate(
		api.updateContent,
	);

	const [error, setError] = useState({
		title: '',
		markdownText: '',
	});

  function clearError(key: keyof typeof error) {
	  setError(prev => ({...prev, [key]: ""}))
	}

	function validate() {
		const markdownText = markdownPreview ? state.markdownText : editorRef.current.getValue();
		const result = validateList([
			{
				name: 'title',
				value: state.title,
				rules: [
					{
						message: "Title can't be empty",
						fn: (value: string) => value.trim().length <= 0,
					},
				],
			},
			{
				name: 'markdownText',
				value: markdownText,
				rules: [
					{
						message: "Markdown value can't be empty",
						fn: (value: string) => value.trim().length <= 0,
					},
				],
			},
		]);
		const hasError = Object.keys(result).length !== 0
		if(hasError) {
		  setError(prev => ({ ...prev, ...result }))
		}
		if(result.markdownText) {
		  WarningToast(result.markdownText)
		}
		return !hasError
	}

	return (
		<Drawer
			mode={mode}
			isPreview={isPreview}
			loading={createLoading || updateLoading}
			visible={isOpen}
			contentType="markdown"
			onClose={() => {
				setState({
					title: '',
					markdownText: '',
				});
				close();
			}}
			onSave={async () => {
				if (mode !== 'view' && !validate()) {
					return;
				}

				const markdownText = markdownPreview ? state.markdownText : editorRef.current.getValue();
				const payload = {
					branchId,
					contentType: 'markdown',
					contentValue: {
						title: state.title.trim(),
						markdownText,
					},
				};

				try {
					if (mode === 'edit') {
						await updateMutate(appId, initialValue?.id, payload);
					} else if (mode === 'create') {
						const res = await createMutate(appId, payload);
						if (onCreate) {
							onCreate(res.contentId);
						}
					} else {
						setMode('edit');
						return;
					}
					refetchContent();
					close();
				} catch (err) {
          ErrorToast(errorMessage(mode, 'markdown', err.code))
				}
			}}
		>
			<div className={style.addMarkdown}>
				<div style={{ position: "relative" }}>
					<label htmlFor="title">Title</label>
					<input
						value={state.title}
						disabled={mode === 'view'}
						autoFocus
						id="title"
						placeholder="Enter title"
						onChange={(e) => {
							clearError("title")
							const title = e.currentTarget.value;
							setState((prev) => ({ ...prev, title }));
						}}
					/>
					<span className={style.error}>{error.title}</span>
				</div>

				<div style={{ marginTop: error.title ? '8px' : '0' }}>
					<div className={style.codeEditorHeader}>
						<div className={style.labelWrapper}>
							<img src={MarkdownIcon} alt="code" />
							<label>Markdown</label>
						</div>
						<div
							className={style.previewToggle}
							style={
								{
									'--checked-bg': markdownPreview ? '#F1722E' : '',
								} as any
							}
						>
							<p>Preview</p>
							<Switch
								checked={markdownPreview}
								onSwitch={(markdownPreview) => {
									setIsMarkdownPreview(markdownPreview)
									if(markdownPreview) {
										const editorValue = editorRef.current.getValue();
										setState(prev => ({...prev, markdownText: editorValue}))
									}
								}}
							/>
						</div>
					</div>

					<div className={style.codeEditorWrapper} data-preview={markdownPreview}>
						{markdownPreview ? (
							<div
								className="markdown-body"
								dangerouslySetInnerHTML={{
									__html: DOMPurify.sanitize(marked.parse(state.markdownText)),
								}}
							></div>
						) : (
							<Editor
								options={{
									minimap: {
										enabled: false,
									},
									tabSize: 2,
									fontSize: 14,
									lineNumbers: 'off',
									scrollBeyondLastLine: false,
									readOnly: mode === 'view',
									renderLineHighlight: 'none',
									glyphMargin: false,
									folding: false,
									lineDecorationsWidth: 0,
									lineNumbersMinChars: 0,
									wordWrap: 'on',
								}}
								value={state.markdownText}
								language="markdown"
								theme="light"
								keepCurrentModel={false}
								width="100%"
								onChange={() => {
								  clearError("markdownText")
								}}
								onMount={(editor) => {
									editorRef.current = editor;
								}}
							/>
						)}
					</div>
				</div>
			</div>
		</Drawer>
	);
}
